<template>
  <!-- Toggle FAB — fixed, never overlaps the drawer -->
  <v-btn
    v-if="isCustomizeFeatureEnabled"
    class="customize-fab"
    icon
    size="large"
    color="customizer-primary"
    elevation="8"
    :style="customizeFabStyle"
    @click="isOpen = !isOpen"
  >
    <v-icon>{{ isOpen ? 'mdi-close' : 'mdi-palette-outline' }}</v-icon>
  </v-btn>

  <!-- Right-side navigation drawer — pushes page content, no scrim -->
  <v-navigation-drawer
    v-if="isCustomizeFeatureEnabled"
    v-model="isOpen"
    location="right"
    :width="drawerWidth"
    temporary
    :scrim="false"
    class="customize-drawer"
    color="customizer-surface"
  >
    <div class="customizer-sticky-header">
      <!-- Header -->
      <v-toolbar density="compact" color="customizer-primary">
        <v-icon class="ml-4" color="customizer-accent">mdi-tune</v-icon>
        <v-toolbar-title class="text-white font-weight-bold ml-2">Live Customizer</v-toolbar-title>
        <v-spacer />
        <v-btn icon variant="text" color="white" @click="isOpen = false">
          <v-icon>mdi-chevron-right</v-icon>
        </v-btn>
      </v-toolbar>

      <template v-if="isEditorReady">
        <v-card variant="flat" class="ma-3 mb-0 customization-edit-header">
          <v-card-text class="pa-3">
            <div class="d-flex align-center justify-space-between mb-3">
              <div>
                <div class="text-caption text-medium-emphasis">Editing</div>
                <div class="text-subtitle-2 font-weight-bold">{{ selectedCustomizationLabel }}</div>
              </div>

              <v-btn
                size="small"
                variant="tonal"
                color="primary"
                prepend-icon="mdi-arrow-left"
                @click="backToCustomizationList"
              >
                Back to list
              </v-btn>
            </div>

            <v-text-field
              v-model="store.customization.name"
              label="Customization name"
              prepend-inner-icon="mdi-form-textbox"
              variant="outlined"
              density="compact"
              hide-details="auto"
              :rules="customizationNameRules"
              required
            />

            <v-alert
              v-if="hasUnsavedChanges"
              type="warning"
              variant="tonal"
              density="compact"
              class="mt-3"
            >
              You have unsaved changes.
            </v-alert>

            <v-alert
              v-if="customizationSaveError"
              type="error"
              variant="tonal"
              density="compact"
              class="mt-3"
            >
              {{ customizationSaveError }}
            </v-alert>
          </v-card-text>
        </v-card>

        <!-- Tabs -->
        <v-tabs v-model="activeTab" bg-color="customizer-primary" color="customizer-accent" density="compact">
          <v-tab value="brand" color="on-customizer-primary" base-color="customizer-surface-variant">
            <v-icon start size="small">mdi-palette</v-icon>Brand
          </v-tab>
          <v-tab
            value="media"
            color="on-customizer-primary"
            base-color="customizer-surface-variant"
          >
            <v-icon start size="small">mdi-image</v-icon>Media
          </v-tab>
          <v-tab value="form" color="on-customizer-primary" base-color="customizer-surface-variant">
            <v-icon start size="small">mdi-form-select</v-icon>Form
          </v-tab>
          <v-tab value="email" color="on-customizer-primary" base-color="customizer-surface-variant">
            <v-icon start size="small">mdi-email-outline</v-icon>Email
          </v-tab>
        </v-tabs>

        <v-divider />
      </template>
    </div>

    <div v-if="!isEditorReady" class="customization-selector pa-0">
      <v-card variant="flat" rounded="lg" class="customization-selector-card">
        <div class="customization-selector-card__header">
          <v-card-title class="d-flex align-center ga-2 text-subtitle-1 font-weight-bold">
            <v-icon class="text-customizer-primary">mdi-palette-swatch-outline</v-icon>
            Customization Records
          </v-card-title>
          <v-card-subtitle>
            Select an existing record or create a new customization before editing tools load.
          </v-card-subtitle>
        </div>

        <v-divider />

        <v-card-text class="customization-selector-card__body">
          <v-alert
            v-if="!resolvedOfficeId"
            type="warning"
            variant="tonal"
            density="compact"
            class="mb-4"
          >
            Missing office ID. Add <strong>?customize=true&amp;office_id=YOUR_OFFICE_ID</strong> to load stored customizations.
          </v-alert>

          <v-alert
            v-else-if="!resolvedSessionId"
            type="warning"
            variant="tonal"
            density="compact"
            class="mb-4"
          >
            Missing session. Add <strong>&amp;session_id=YOUR_SESSION_ID</strong> to load stored customizations.
          </v-alert>

          <template v-else>
            <div class="d-flex align-center justify-space-between mb-3">
              <span class="text-caption text-medium-emphasis">Office ID</span>
              <v-chip size="small" color="primary" variant="tonal">{{ resolvedOfficeId }}</v-chip>
            </div>

            <v-progress-linear
              v-if="customizationsLoading"
              indeterminate
              color="primary"
              class="mb-4"
            />

            <v-alert
              v-if="customizationListError"
              type="error"
              variant="tonal"
              density="compact"
              class="mb-4"
            >
              {{ customizationListError }}
            </v-alert>

            <v-alert
              v-if="!customizationsLoading && !customizationListError && !customizationRecords.length"
              type="info"
              variant="tonal"
              density="compact"
              class="mb-4"
            >
              No saved customizations were found for this Office Code yet.
            </v-alert>

            <div
              v-if="customizationRecords.length"
              class="customization-record-card-list mb-4"
            >
              <v-card
                v-for="record in customizationRecords"
                :key="record.id"
                class="customization-record-card"
                elevation="2"
                :variant="String(record.state || '').toLowerCase() === 'published' ? 'tonal' : 'flat'"
                :color="String(record.state || '').toLowerCase() === 'published' ? 'customizer-secondary' : 'customizer-surface-variant'"
                rounded="lg"
                :theme="String(record.state || '').toLowerCase() === 'published' ? 'dark' : 'light'"
              >
                <v-card-title class="customization-record-card__title">
                  <div class="d-flex align-center ga-2 min-w-0">
                    <v-avatar color="primary" variant="tonal" size="34">
                      <v-icon size="small">mdi-palette-outline</v-icon>
                    </v-avatar>
                    <span class="customization-record-card__name">
                      {{ record.customizationName || record.name || 'Untitled customization' }}
                    </span>
                  </div>

                  <v-chip
                    size="x-small"
                    :color="String(record.state || '').toLowerCase() === 'published' ? 'customizer-primary' : 'primary'"
                    :variant="String(record.state || '').toLowerCase() === 'published' ? 'flat' : 'tonal'"
                    :class="{ 'font-weight-bold': String(record.state || '').toLowerCase() === 'published' }"
                  >
                    {{ record.state || 'Draft' }}
                  </v-chip>
                </v-card-title>

                <v-card-text class="customization-record-card__meta">
                  <div>
                    <span>ID</span>
                    <strong>{{ record.id }}</strong>
                  </div>
                  <div>
                    <span>Created</span>
                    <strong>{{ record.createdDate || '—' }}</strong>
                  </div>
                  <div v-if="record.createdByName">
                    <span>Created by</span>
                    <strong>{{ record.createdByName }}</strong>
                  </div>
                  <div>
                    <span>Updated</span>
                    <strong>{{ record.updatedDate || '—' }}</strong>
                  </div>
                  <div v-if="record.updatedByName">
                    <span>Updated by</span>
                    <strong>{{ record.updatedByName }}</strong>
                  </div>
                </v-card-text>

                <v-card-actions class="customization-record-card__actions">
                  <v-btn
                    size="small"
                    color="primary"
                    variant="flat"
                    block
                    append-icon="mdi-arrow-right"
                    @click="selectCustomizationRecord(record)"
                  >
                    Open customization
                  </v-btn>
                </v-card-actions>
              </v-card>
            </div>
          </template>
        </v-card-text>

        <v-divider />

        <v-card-actions class="customization-selector-card__footer">
          <v-btn
            color="primary"
            variant="flat"
            prepend-icon="mdi-plus"
            :disabled="!resolvedOfficeId || !resolvedSessionId || !resolvedAuthorId"
            @click="createCustomizationStub"
          >
            Create customization
          </v-btn>
          <v-spacer />
          <v-btn
            v-if="resolvedOfficeId && resolvedSessionId"
            size="small"
            variant="text"
            :loading="customizationsLoading"
            @click="loadCustomizationRecords"
          >
            Refresh
          </v-btn>
        </v-card-actions>
      </v-card>
    </div>

    <template v-else>
      <div class="customizer-editor-shell">
        <!-- Tab Windows -->
        <v-window v-model="activeTab" class="overflow-y-auto customizer-window">

        <!-- ─────────────────────────────────────────────
             TAB 1 · BRAND
        ───────────────────────────────────────────── -->
        <v-window-item value="brand">
          <v-container fluid class="pa-4">
            <p class="text-caption text-medium-emphasis mb-4">
              Changes apply live to the page. Each pair shows the <strong>surface color</strong> (large)
              and the <strong>text on that surface</strong> (small).
            </p>

            <v-card variant="outlined" class="pa-3 mb-4 header-settings-card">
              <p class="text-subtitle-2 font-weight-bold mb-2">Header Settings</p>
              <p class="text-caption text-medium-emphasis mb-3">
                Control the live page header background and overall height.
              </p>

              <ColorPairPicker
                label="Header background"
                hint="Header"
                :surface-color="headerColorPreview"
                :on-color="headerTextColorPreview"
                surface-var="header-color"
                on-var="header-text-color"
                @update:surface-color="onHeaderColorInput"
                @update:on-color="onHeaderTextColorInput"
              />

              <v-card variant="outlined" rounded="lg" class="pa-3 mt-3 header-height-card">
                <v-row dense class="align-center">
                  <v-col cols="12" sm="5">
                    <p class="text-caption mb-1">Header Height (px)</p>
                    <v-text-field
                      :model-value="headerHeightPreview"
                      type="number"
                      min="72"
                      max="180"
                      step="1"
                      density="compact"
                      variant="outlined"
                      color="customizer-primary"
                      hide-details
                      suffix="px"
                      @update:model-value="onHeaderHeightInput"
                    />
                  </v-col>

                  <v-col cols="12" class="mt-2">
                    <p class="text-caption mb-1">Header Height: <strong>{{ headerHeightPreview }}px</strong></p>
                    <v-slider
                      :model-value="headerHeightPreview"
                      min="72"
                      max="180"
                      step="1"
                      hide-details
                      color="customizer-primary"
                      track-color="customizer-surface-variant"
                      density="compact"
                      @update:model-value="onHeaderHeightInput"
                    />
                  </v-col>
                </v-row>
              </v-card>
            </v-card>

            <div class="d-flex flex-column gap-3">
              <!-- Background + on-background -->
              <ColorPairPicker
                label="Page background "
                hint="Background"
                :surface-color="colors.background"
                :on-color="colors['on-background']"
                surface-var="background"
                on-var="on-background"
                @update:surface-color="val => applyColor('background', val)"
                @update:on-color="val => applyColor('on-background', val)"
              />

              <!-- Surface + on-surface -->
              <ColorPairPicker
                label="Cards & sheets"
                hint="Surface"
                :surface-color="colors.surface"
                :on-color="colors['on-surface']"
                surface-var="surface"
                on-var="on-surface"
                @update:surface-color="val => applyColor('surface', val)"
                @update:on-color="val => applyColor('on-surface', val)"
              />

            
              <!-- Surface Light (panels) -->
              <ColorPairPicker
                label="All panels"
                hint="Panels (Surface Light)"
                :surface-color="colors['surface-light']"
                :on-color="colors['on-surface-light']"
                surface-var="surface-light"
                on-var="on-surface-light"
                @update:surface-color="val => applyColor('surface-light', val)"
                @update:on-color="val => applyColor('on-surface-light', val)"
              />

              <!-- Surface Variant (footer) -->
              <ColorPairPicker
                label="Footer"
                hint="Surface Variant"
                :surface-color="colors['surface-variant']"
                :on-color="colors['on-surface-variant']"
                surface-var="surface-variant"
                on-var="on-surface-variant"
                @update:surface-color="val => applyColor('surface-variant', val)"
                @update:on-color="val => applyColor('on-surface-variant', val)"
              />

              <!-- Surface Bright -->
              <ColorPairPicker
                label="Results panel"
                hint="Surface Bright"
                :surface-color="colors['surface-bright']"
                :on-color="colors['on-surface-bright']"
                surface-var="surface-bright"
                on-var="on-surface-bright"
                @update:surface-color="val => applyColor('surface-bright', val)"
                @update:on-color="val => applyColor('on-surface-bright', val)"
              />


              <!-- Primary + text -->
              <ColorPairPicker
                label="CTAs"
                hint="Primary"
                :surface-color="colors.primary"
                :on-color="colors['on-primary']"
                surface-var="primary"
                on-var="on-primary"
                @update:surface-color="val => applyColor('primary', val)"
                @update:on-color="val => applyColor('on-primary', val)"
              />

              <!-- Secondary -->
              <ColorPairPicker
                label="Secondary CTA / Icons (color only, no text)"
                hint="Secondary"
                :surface-color="colors.secondary"
                :on-color="colors.white"
                surface-var="secondary"
                on-var="on-secondary"
                @update:surface-color="val => applyColor('secondary', val)"
                @update:on-color="val => applyColor('on-secondary', val)"
              />

              <!-- Accent -->
              <ColorPairPicker
                label="Hero accent text"
                hint="Accent"
                :surface-color="colors.accent"
                surface-var="accent"
                single
                @update:surface-color="val => applyColor('accent', val)"
              />

            </div>

            <v-divider class="my-4" />

            <v-row>
              <v-col cols="12" class="d-flex gap-2 flex-wrap">
                <v-btn size="small" variant="tonal" color="error" prepend-icon="mdi-restore" @click="resetColors">
                  Reset colors
                </v-btn>
                <v-btn size="small" variant="tonal" color="primary" prepend-icon="mdi-content-copy" @click="copyCSS">
                  Copy CSS vars
                </v-btn>
                <v-btn size="small" variant="tonal" color="error" prepend-icon="mdi-refresh" @click="resetAllToGlobal">
                  Reset all to global
                </v-btn>
              </v-col>
            </v-row>
          </v-container>
        </v-window-item>

        <!-- ─────────────────────────────────────────────
             TAB 2 · MEDIA
        ───────────────────────────────────────────── -->
        <v-window-item value="media">
          <v-container fluid class="pa-4">
            <v-alert type="info" variant="tonal" density="compact" class="mb-4">
              Hero image uploads through the customization API. Video URLs are restricted to Vimeo or YouTube.
            </v-alert>

            <v-row dense>
              <!-- Hero image -->
              <v-col cols="12">
                <p class="text-subtitle-2 font-weight-bold mb-1">Hero Image</p>

                <!-- ── Stored image info (loaded from DB or just uploaded) ── -->
                <template v-if="heroImageStoredUrl && !media.isReplacingHero && !media.isUploadingHero">
                  <div class="hero-stored-card">
                    <v-icon size="16" color="success" class="flex-shrink-0">mdi-check-circle</v-icon>
                    <div class="hero-stored-card__text min-w-0">
                      <div class="text-caption font-weight-bold text-truncate">{{ heroImageFilename }}</div>
                      <div class="text-caption text-medium-emphasis text-truncate">Stored hero image</div>
                    </div>
                  </div>
                  <div class="d-flex ga-2 mt-2">
                    <v-btn
                      size="small"
                      variant="tonal"
                      color="primary"
                      prepend-icon="mdi-swap-horizontal"
                      @click="media.isReplacingHero = true"
                    >
                      Replace
                    </v-btn>
                    <v-btn
                      size="small"
                      variant="tonal"
                      color="error"
                      prepend-icon="mdi-image-remove"
                      @click="removeHeroImage"
                    >
                      Remove
                    </v-btn>
                  </div>
                </template>

                <!-- ── File picker (no image yet, replacing, or uploading) ── -->
                <template v-else>
                  <v-file-input
                    v-model="media.heroImageFile"
                    label="Upload hero image"
                    prepend-icon="mdi-image-plus"
                    accept="image/*"
                    variant="outlined"
                    density="compact"
                    hide-details="auto"
                    :loading="media.isUploadingHero"
                    :disabled="media.isUploadingHero"
                    @update:model-value="onHeroImageSelected"
                  />
                  <v-btn
                    v-if="media.isReplacingHero && !media.isUploadingHero"
                    size="small"
                    variant="text"
                    color="secondary"
                    class="mt-1 px-0"
                    @click="cancelReplaceHero"
                  >
                    ← Cancel
                  </v-btn>
                </template>

                <div v-if="media.heroImageUploadError" class="text-error text-caption mt-1">
                  {{ media.heroImageUploadError }}
                </div>
                <v-alert
                  v-if="media.heroImageReplaceSuccess && !hasUnsavedChanges"
                  type="success"
                  variant="tonal"
                  density="compact"
                  class="mt-2"
                  closable
                  @click:close="media.heroImageReplaceSuccess = false"
                >
                  Image replaced successfully. No configuration changes to save.
                </v-alert>
                <v-img
                  v-if="heroImagePreview"
                  :src="heroImagePreview"
                  class="mt-3 media-preview"
                  cover
                />
              </v-col>

              <!-- Hero Overlay -->
              <v-col cols="12" class="mt-4">
                <p class="text-subtitle-2 font-weight-bold mb-1">Hero Image Overlay</p>
                <p class="text-caption text-medium-emphasis mb-3">Tint the hero background to improve text legibility.</p>

                <v-row dense align="center">
                  <v-col cols="12" sm="7">
                    <p class="text-caption mb-1" style="line-height:1.2">
                      Overlay Color<br /><code class="text-caption">{{ media.overlayColor || '—' }}</code>
                    </p>
                    <div class="d-flex align-center ga-2">
                      <label
                        class="overlay-color-swatch"
                        :style="{ background: media.overlayColor || '#000000', opacity: media.overlayColor ? 1 : 0.3 }"
                      >
                        <input
                          type="color"
                          :value="media.overlayColor || '#000000'"
                          class="hidden-color-input"
                          @input="e => onOverlayColorInput(e.target.value)"
                        />
                      </label>
                      <v-text-field
                        :model-value="media.overlayColor || ''"
                        placeholder="#000000"
                        density="compact"
                        variant="outlined"
                        hide-details
                        maxlength="7"
                        style="font-size:12px; max-width: 110px;"
                        @update:model-value="val => isValidOverlayHex(val) && onOverlayColorInput(val)"
                      />
                    </div>
                  </v-col>

                  <v-col v-if="media.overlayColor" cols="12" class="mt-2">
                    <p class="text-caption mb-1">Opacity: <strong>{{ media.overlayOpacity ?? 40 }}%</strong></p>
                    <v-slider
                      :model-value="media.overlayOpacity ?? 40"
                      min="5"
                      max="95"
                      step="5"
                      hide-details
                      color="customizer-primary"
                      track-color="customizer-surface-variant"
                      density="compact"
                      @update:model-value="val => onOverlayOpacityInput(val)"
                    />
                  </v-col>

                  <v-col v-if="media.overlayColor" cols="12" class="mt-1">
                    <v-btn size="x-small" variant="text" color="error" prepend-icon="mdi-close" @click="clearOverlay">
                      Remove overlay
                    </v-btn>
                  </v-col>
                </v-row>
              </v-col>

              <!-- Video Source -->
              <v-col cols="12" class="mt-3">
                <p class="text-subtitle-2 font-weight-bold mb-1">Video Source</p>
                <v-select
                  v-model="media.videoSource"
                  :items="videoSourceOptions"
                  item-title="title"
                  item-value="value"
                  prepend-inner-icon="mdi-source-branch"
                  variant="outlined"
                  density="compact"
                  hide-details="auto"
                  :list-props="{ bgColor: 'customizer-surface' }"
                  @update:model-value="applyVideoSelection"
                />
              </v-col>

              <!-- Video URL -->
              <v-col cols="12" class="mt-3">
                <p class="text-subtitle-2 font-weight-bold mb-1">Video URL</p>
                <v-text-field
                  v-model="media.videoUrl"
                  :label="`${media.videoSource === 'youtube' ? 'YouTube' : 'Vimeo'} URL`"
                  prepend-inner-icon="mdi-video-outline"
                  variant="outlined"
                  density="compact"
                  :rules="videoUrlRules"
                  hide-details="auto"
                  :placeholder="media.videoSource === 'youtube' ? 'https://www.youtube.com/watch?v=…' : 'https://vimeo.com/…'"
                  @blur="applyVideoSelection"
                />
                <v-btn class="mt-2" size="small" color="primary" variant="tonal" @click="applyVideoSelection">
                  Apply Video URL
                </v-btn>
                <div v-if="videoPreviewEmbedUrl" class="mt-3 media-video-wrap">
                  <iframe
                    :src="videoPreviewEmbedUrl"
                    frameborder="0"
                    allow="autoplay; fullscreen; picture-in-picture"
                    allowfullscreen
                  ></iframe>
                </div>
              </v-col>

              <v-col cols="12" class="mt-2">
                <v-btn size="small" variant="tonal" color="error" prepend-icon="mdi-image-remove" @click="resetMedia">
                  Reset Media
                </v-btn>
              </v-col>
            </v-row>
          </v-container>
        </v-window-item>

        <!-- ─────────────────────────────────────────────
             TAB 3 · FORM OPTIONS
        ───────────────────────────────────────────── -->
        <v-window-item value="form">
          <v-container fluid class="pa-4">
            <v-alert type="info" variant="tonal" density="compact" class="mb-4">
              Control which lead capture fields are required in the template form.
            </v-alert>

            <v-row dense>
              <v-col cols="12">
                <v-switch
                  v-model="store.customization.formOptions.phoneRequired"
                  color="customizer-primary"
                  inset
                  base-color="customizer-surface-variant"
                  hide-details="auto"
                  label="Require Phone Number"
                  @update:model-value="markFieldTouched('phone_required')"
                />
                <p class="text-caption text-medium-emphasis mt-1">
                  When enabled, visitors must enter a valid phone number before submitting the form.
                </p>
              </v-col>
            </v-row>
          </v-container>
        </v-window-item>

        <!-- ─────────────────────────────────────────────
             TAB 4 · EMAIL
        ───────────────────────────────────────────── -->
        <v-window-item value="email">
          <v-container fluid class="pa-4">
            <v-alert type="info" variant="tonal" density="compact" class="mb-4">
              Configure the Tax Bill report email. These settings are stored with the customization record and do not change the page appearance.
            </v-alert>

            <v-row dense>
              <v-col cols="12">
                <p class="text-subtitle-2 font-weight-bold mb-1">Attachment Behavior</p>
                <v-switch
                  v-model="store.customization.email.attachPdfReport"
                  color="customizer-primary"
                  inset
                  base-color="customizer-surface-variant"
                  hide-details="auto"
                  label="Attach PDF report"
                  @update:model-value="markFieldTouched('attach_pdf')"
                />
                <p
                  v-if="!store.customization.email.attachPdfReport"
                  class="text-caption text-medium-emphasis mt-1"
                >
                  <v-icon>mdi-alert-circle</v-icon>The email will be send without the Generated Report PDF attachment.
                </p>
              </v-col>

              <v-col cols="12">
                <p class="text-subtitle-2 font-weight-bold mb-1">Subject Line</p>
                <v-text-field
                  v-model="store.customization.email.subject"
                  label="Email subject"
                  prepend-inner-icon="mdi-format-title"
                  variant="outlined"
                  density="compact"
                  hide-details="auto"
                  @update:model-value="markFieldTouched('email_subject')"
                />
              </v-col>

              <v-col cols="12" class="mt-3">
                <p class="text-subtitle-2 font-weight-bold mb-1">Email Body</p>
                <EmailBodyEditor
                  v-model="store.customization.email.bodyHtml"
                  :merge-tag-groups="store.customization.email.mergeTagGroups"
                  @update:model-value="markFieldTouched('email_body')"
                />
              </v-col>

              <v-col cols="12" class="mt-2">
                <v-btn
                  size="small"
                  variant="tonal"
                  color="customizer-primary"
                  prepend-icon="mdi-eye-outline"
                  @click="showEmailPreview = true"
                >
                  Preview email
                </v-btn>
              </v-col>

              <v-col cols="12" class="mt-3">
                <p class="text-caption text-medium-emphasis mb-2">Starter merge Tokens</p>
                <template v-for="group in store.customization.email.mergeTagGroups" :key="group.category">
                  <p class="text-caption text-medium-emphasis mb-1 mt-2">{{ group.category }}</p>
                  <div class="d-flex flex-wrap ga-2 mb-2">
                    <v-chip
                      v-for="tag in group.tags"
                      :key="tag.token"
                      size="small"
                      color="primary"
                      variant="tonal"
                    >
                      {{ tag.token }}
                    </v-chip>
                  </div>
                </template> 
              </v-col>
            </v-row>
          </v-container>
        </v-window-item>

        </v-window>

        <v-divider />

        <div class="customizer-save-footer">
          <div class="customizer-save-footer__meta">
            <span>{{ footerStatusText }}</span>
            <v-chip
              v-if="store.customization.id"
              size="x-small"
              color="primary"
              variant="tonal"
            >
              ID: {{ store.customization.id }}
            </v-chip>
          </div>

          <div class="customizer-save-footer__actions">
            <v-btn
              color="primary"
              variant="tonal"
              prepend-icon="mdi-content-save-outline"
              block
              :loading="customizationSaveAction === 'preview'"
              :disabled="isPreviewSaveDisabled"
              @click="saveCustomization('preview')"
            >
              Apply changes
            </v-btn>

            <v-btn
              v-if="store.customization.state !== 'published'"
              color="customizer-primary"
              variant="flat"
              prepend-icon="mdi-cloud-upload-outline"
              block
              :loading="customizationSaveAction === 'published'"
              :disabled="isPublishSaveDisabled"
              @click="saveCustomization('published')"
            >
              Publish
            </v-btn>

            <v-btn
              v-else
              color="customizer-primary"
              variant="flat"
              prepend-icon="mdi-cloud-off-outline"
              block
              :loading="customizationSaveAction === 'unpublished'"
              :disabled="isUnpublishDisabled"
              @click="unpublishCustomization"
            >
              Un-publish
            </v-btn>
          </div>
        </div>

      </div>

      <!-- Email Preview Dialog -->
      <v-dialog v-model="showEmailPreview" max-width="660" scrollable>
        <v-card color="customizer-surface">
          <v-card-title class="d-flex align-center justify-space-between pa-4 pb-2">
            <span class="text-subtitle-1 font-weight-bold">Email Preview</span>
            <v-btn icon variant="text" size="small" @click="showEmailPreview = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>

          <v-alert
            v-if="emailPreviewContent.usingSampleTokens.length"
            type="info"
            variant="tonal"
            density="compact"
            class="mx-4 mb-2"
          >
            Visitor tokens <strong>{{ emailPreviewContent.usingSampleTokens.join(', ') }}</strong> are shown with sample data — they'll be replaced with real values when sent.
          </v-alert>

          <v-card-text class="pa-4 pt-2">
            <p class="text-caption text-medium-emphasis mb-1">Subject</p>
            <v-sheet
              border
              rounded
              color="customizer-surface"
              class="pa-3 mb-4 text-body-2"
            >
              {{ emailPreviewContent.resolvedSubject || '(no subject)' }}
            </v-sheet>

            <p class="text-caption text-medium-emphasis mb-1">Body</p>
            <v-sheet rounded border class=" bg-customizer-background email-preview-frame-wrapper">
              <iframe
                :srcdoc="emailPreviewContent.iframeDoc"
                sandbox="allow-same-origin"
                class="email-preview-frame"
                frameborder="0"
              />
            </v-sheet>
          </v-card-text>
        </v-card>
      </v-dialog>

    </template>

    <v-overlay
      absolute
      contained
      persistent
      :model-value="showCreateCustomizationOverlay"
      scrim="customizer-surface"
      opacity="0.78"
      class="customizer-create-overlay d-flex align-center justify-center"
    >
      <v-card class="pa-2 customizer-create-overlay__card text-on-secondary elevation-10" color="customizer-primary" rounded="lg" elevation="10">
        <v-card-title>
          <div class="text-subtitle-1 font-weight-bold mb-2">Create customization</div>
        </v-card-title>
        <v-divider />
        <v-card-text class="pa-5">
          
          <div class="text-caption on-secondary text-medium-emphasis mb-4">
            Add a name, then create the record to generate the customization ID.
          </div>

          <v-text-field
            v-model="store.customization.name"
            label="Customization name"
            prepend-inner-icon="mdi-form-textbox"
            variant="outlined"
            density="compact"
            hide-details="auto"
            :rules="customizationNameRules"
            class="mb-4"
          />

          <v-alert
            v-if="createCustomizationError"
            type="error"
            variant="tonal"
            density="compact"
            class="mb-4"
          >
            {{ createCustomizationError }}
          </v-alert>

          <v-btn
            color="success"
            variant="flat"
            prepend-icon="mdi-plus"
            block
            :loading="customizationSaveAction === 'create'"
            :disabled="isCreateCustomizationDisabled"
            @click="createCustomizationFromEditor"
          >
            Create customization
          </v-btn>
        </v-card-text>
      </v-card>
    </v-overlay>
  </v-navigation-drawer>
</template>

<script setup>
import { ref, reactive, computed, watch } from 'vue'
import { useRoute } from 'vue-router'
import axios from 'axios'
import Swal from 'sweetalert2'
import ColorPairPicker from './editor/ColorPairPicker.vue'
import EmailBodyEditor from './editor/EmailBodyEditor.vue'
import { useLeadGeneratorStore } from '@/stores/leadGenerator'
import { useBranding } from '@/composables/useBranding'
import { DEFAULT_EMBED_VIDEO_URL, getVideoEmbedInfo } from '@/utils/mediaEmbed'
import {
  buildUploadCustomizationImageUrl,
  buildUpdateOfficeCustomizationUrl,
  buildListCustomizationsUrl,
  getSavedCustomizationId,
  normalizeCustomizationListResponse,
  resolveEmailBody,
} from '@/utils/customization'
import {
  applyThemeOverrides,
  clearThemeOverrides,
  DEFAULT_BRAND_COLORS,
  getHydratedBrandColors,
  hexToRgbComponents,
  sanitizeBrandTheme,
} from '@/utils/themeOverrides'
const props = defineProps({
  drawerWidth: {
    type: Number,
    default: 480,
  },
  isCustomizeMode: {
    type: Boolean,
    default: false,
  },
})

const CUSTOMIZER_STATE_STORAGE_KEY = 'rtb_customizer_ui_state'

function readCustomizerUiState() {
  if (typeof window === 'undefined') return {}

  try {
    return JSON.parse(window.sessionStorage.getItem(CUSTOMIZER_STATE_STORAGE_KEY) || '{}')
  } catch {
    return {}
  }
}

function persistCustomizerUiState(value) {
  if (typeof window === 'undefined') return
  window.sessionStorage.setItem(CUSTOMIZER_STATE_STORAGE_KEY, JSON.stringify(value))
}

function clearCustomizerUiState() {
  if (typeof window === 'undefined') return
  window.sessionStorage.removeItem(CUSTOMIZER_STATE_STORAGE_KEY)
}

// ── State ─────────────────────────────────────────────────────────────────────
const isOpen = defineModel('open', { type: Boolean, default: true })
const persistedCustomizerUiState = readCustomizerUiState()
const activeTab = ref(persistedCustomizerUiState.activeTab || 'brand')
const store = useLeadGeneratorStore()
const route = useRoute()
const { loadBranding, brandingCustomizationId } = useBranding()
const showEmailPreview = ref(false)

// Sample visitor values used when store.user fields are empty (preview context)
const PREVIEW_SAMPLE_VISITOR = {
  '[FirstName]': 'Jane',
  '[LastName]': 'Smith',
}

const VISITOR_STORE_MAP = () => ({
  '[FirstName]': store.user?.first_name,
  '[LastName]': store.user?.last_name,
})

const emailPreviewContent = computed(() => {
  const bodyHtml = store.customization.email.bodyHtml || ''
  const subject = store.customization.email.subject || ''

  // Merge real visitor data with samples for any empty fields
  const storeMap = VISITOR_STORE_MAP()
  const visitorOverrides = Object.fromEntries(
    Object.entries(PREVIEW_SAMPLE_VISITOR).map(([token, sample]) => [
      token, storeMap[token] || sample,
    ])
  )

  const resolvedBody = resolveEmailBody(bodyHtml, store, visitorOverrides)
  const resolvedSubject = resolveEmailBody(subject, store, visitorOverrides)

  // Determine which tokens were filled from samples vs real data
  const usingSampleTokens = Object.entries(PREVIEW_SAMPLE_VISITOR)
    .filter(([token]) => !VISITOR_STORE_MAP()[token])
    .map(([token]) => token)

  // Wrap the resolved HTML in a basic document shell for iframe rendering
  const iframeDoc = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <style>
    body { font-family: Arial, sans-serif; font-size: 15px; line-height: 1.6;
           color: #222; max-width: 600px; margin: 0 auto; padding: 24px 16px; }
    a { color: #1a73e8; }
    img { max-width: 100%; height: auto; }
    ol, ul { padding-left: 1.5em; }
  </style>
</head>
<body>${resolvedBody}</body>
</html>`

  return { resolvedSubject, iframeDoc, usingSampleTokens }
})
store.ensureCustomizationDefaults()

const isEditorReady = ref(Boolean(
  persistedCustomizerUiState.isEditorReady &&
  store.customizationLoaded &&
  (store.customization?.id || store.customization?.name)
))
const customizationsLoading = ref(false)
const customizationListError = ref('')
const customizationRecords = ref([])
const selectedCustomizationRecord = ref(
  persistedCustomizerUiState.selectedCustomizationRecord ||
  (isEditorReady.value
    ? {
      id: store.customization?.id || 'customization-in-progress',
      name: store.customization?.name || 'Customization in progress',
      customizationName: store.customization?.name || 'Customization in progress',
      state: store.customization?.state || 'preview',
    }
    : null)
)
  const activeOfficeId = ref(persistedCustomizerUiState.accessKey || '')
const createCustomizationError = ref('')
const customizationEditSnapshot = ref('')
const touchedFields = ref(new Set())
const customizationSaveAction = ref('')
const customizationSaveError = ref('')
let customizationListRequestId = 0
let activeCustomizationErrorAlert = ''

const CUSTOMIZER_SWAL_OPTIONS = {
  customClass: {
    container: 'customizer-swal-container',
    popup: 'customizer-swal-popup',
    confirmButton: 'customizer-swal-confirm',
    denyButton: 'customizer-swal-deny',
    cancelButton: 'customizer-swal-cancel',
  },
  buttonsStyling: false,
}

function showCustomizerSwal(options) {
  return Swal.fire({
    ...CUSTOMIZER_SWAL_OPTIONS,
    ...options,
    customClass: {
      ...CUSTOMIZER_SWAL_OPTIONS.customClass,
      ...(options.customClass || {}),
    },
  })
}

const isCustomizeFeatureEnabled = computed(() => props.isCustomizeMode && isCustomizeQueryEnabled() && hasRequiredCustomizeRouteContext())

function getRouteQueryValue(...keys) {
  const routeValue = keys
    .flatMap((key) => [route.query[key], route.query[`amp;${key}`]])
    .map(normalizeQueryValue)
    .find(Boolean)

  if (routeValue) return routeValue

  return getWindowQueryValue(keys) || getRawUrlQueryValue(keys)
}

function normalizeQueryValue(value) {
  const normalizedValue = Array.isArray(value) ? value[0] : value
  return String(normalizedValue || '').trim()
}

function getWindowQueryValue(keys) {
  if (typeof window === 'undefined') return ''

  const normalizedSearch = window.location.search.replace(/&amp;/g, '&')
  const searchSources = [normalizedSearch]
  const hashQueryStart = window.location.hash.indexOf('?')

  if (hashQueryStart >= 0) {
    searchSources.push(window.location.hash.slice(hashQueryStart).replace(/&amp;/g, '&'))
  }

  for (const search of searchSources) {
    const params = new URLSearchParams(search)

    for (const key of keys) {
      const value = params.get(key) || params.get(`amp;${key}`)
      if (value) return String(value).trim()
    }
  }

  return ''
}

function getRawUrlQueryValue(keys) {
  if (typeof window === 'undefined') return ''

  const rawUrl = window.location.href.replace(/&amp;/g, '&')

  for (const key of keys) {
    const pattern = new RegExp(`[?&#]${key}=([^&#]*)`, 'i')
    const match = rawUrl.match(pattern)
    if (match?.[1]) return decodeURIComponent(match[1].replace(/\+/g, ' ')).trim()
  }

  return ''
}

function getRouteSessionId() {
  return getRouteQueryValue('session_id', 'SESSION_ID', 'SessionID', 'sessionId', 'SESSION', 'session')
}

function isCustomizeQueryEnabled() {
  return getRouteQueryValue('customize').toLowerCase() === 'true'
}

function hasRequiredCustomizeRouteContext() {
  const sessionId = getRouteSessionId()
  const officeId = getRouteQueryValue('office_id', 'officeId', 'C', 'c')
  const authorId = getRouteQueryValue('U', 'u')

  return Boolean(sessionId && officeId && authorId)
}

const routeOfficeId = computed(() => getRouteQueryValue('office_id', 'officeId', 'C', 'c'))

const routeAuthorId = computed(() => getRouteQueryValue('U', 'u'))

const resolvedOfficeId = computed(() => {
  const routeCode = routeOfficeId.value
  if (routeCode) sessionStorage.setItem('rtb_c', routeCode)

  const persistedCode = sessionStorage.getItem('rtb_c') || ''
  return String(routeCode || persistedCode || '').trim()
})

const resolvedAuthorId = computed(() => {
  const routeAuthor = routeAuthorId.value
  if (routeAuthor) sessionStorage.setItem('rtb_u', routeAuthor)

  const persistedAuthor = sessionStorage.getItem('rtb_u') || ''
  return String(routeAuthor || persistedAuthor || '').trim()
})

const resolvedSessionId = computed(() => {
  const routeSession = getRouteSessionId()
  if (routeSession) sessionStorage.setItem('rtb_session_id', routeSession)

  if (!routeSession && (routeOfficeId.value || routeAuthorId.value)) return ''

  const persistedSession = sessionStorage.getItem('rtb_session_id') || ''
  return String(routeSession || persistedSession || '').trim()
})

const selectedCustomizationLabel = computed(() => {
  if (store.customization.name) return store.customization.name
  if (selectedCustomizationRecord.value?.customizationName) return selectedCustomizationRecord.value.customizationName
  if (selectedCustomizationRecord.value?.name) return selectedCustomizationRecord.value.name
  return 'new customization draft'
})

const customizationNameRules = [
  (value) => Boolean(String(value || '').trim()) || 'Customization name is required.',
]

const hasUnsavedChanges = computed(() => {
  if (!isEditorReady.value) return false
  return getCustomizationSnapshot() !== customizationEditSnapshot.value
})

const hasTouchedFields = computed(() => touchedFields.value.size > 0)

const isCustomizationNameValid = computed(() => Boolean(String(store.customization.name || '').trim()))

const hasPersistedCustomizationId = computed(() => {
  const id = String(store.customization.id || '').trim()
  return Boolean(id && id !== 'new-customization')
})

const isSavingCustomization = computed(() => Boolean(customizationSaveAction.value))

const isSaveUnavailable = computed(() => (
  isSavingCustomization.value ||
  media.isUploadingHero ||
  !isCustomizationNameValid.value ||
  !resolvedOfficeId.value ||
  !resolvedSessionId.value ||
  !resolvedAuthorId.value
))

const isCreateCustomizationDisabled = computed(() => (
  hasPersistedCustomizationId.value ||
  isSaveUnavailable.value
))

const showCreateCustomizationOverlay = computed(() => (
  isEditorReady.value &&
  !hasPersistedCustomizationId.value &&
  selectedCustomizationRecord.value?.id === 'new-customization'
))

const isPreviewSaveDisabled = computed(() => (
  isSaveUnavailable.value ||
  !hasPersistedCustomizationId.value ||
  !hasUnsavedChanges.value
))

const isPublishSaveDisabled = computed(() => (
  isSaveUnavailable.value ||
  !hasPersistedCustomizationId.value ||
  hasUnsavedChanges.value
))

const isUnpublishDisabled = computed(() => (
  isSaveUnavailable.value ||
  !hasPersistedCustomizationId.value
))

const footerStatusText = computed(() => {
  if (!resolvedOfficeId.value) return 'Missing office C code.'
  if (!resolvedSessionId.value) return 'Missing session ID.'
  if (!resolvedAuthorId.value) return 'Missing author U code.'
  if (media.isUploadingHero) return 'Hero image upload in progress…'
  if (!hasPersistedCustomizationId.value) return 'Name this customization, then click Create customization.'
  if (!isCustomizationNameValid.value) return 'Name this customization before saving.'
  if (hasUnsavedChanges.value) return hasTouchedFields.value ? 'Ready to save customized fields.' : 'Ready to save metadata changes.'
  return 'All changes saved.'
})

const customizeFabStyle = computed(() => ({
  right: isOpen.value ? `calc(var(--sw-customizer-width, 480px) + 24px)` : '24px',
}))

const colors = reactive(getHydratedBrandColors(store.customization.theme))

const videoSourceOptions = [
  { title: 'Vimeo', value: 'vimeo' },
  { title: 'YouTube', value: 'youtube' },
]

const media = reactive({
  heroImageFile: null,
  isUploadingHero: false,
  isReplacingHero: false,
  heroImageReplaceSuccess: false,
  heroImageUploadError: '',
  videoSource: store.customization.media.videoSource || 'vimeo',
  videoUrl: store.customization.media.videoUrl || DEFAULT_EMBED_VIDEO_URL,
  overlayColor: store.customization.media.overlayColor ?? null,
  overlayOpacity: store.customization.media.overlayOpacity ?? null,
})

const DEFAULT_HEADER_COLOR = '#FFFFFF'
const DEFAULT_HEADER_TEXT_COLOR = '#0F172A'
const DEFAULT_HEADER_HEIGHT_PX = 105
const MIN_HEADER_HEIGHT_PX = 72
const MAX_HEADER_HEIGHT_PX = 180

function normalizeHeaderColor(value) {
  const normalized = String(value || '').trim()
  return /^#[0-9A-Fa-f]{6}$/.test(normalized) ? normalized : DEFAULT_HEADER_COLOR
}

function normalizeHeaderTextColor(value) {
  const normalized = String(value || '').trim()
  return /^#[0-9A-Fa-f]{6}$/.test(normalized) ? normalized : DEFAULT_HEADER_TEXT_COLOR
}

function normalizeHeaderHeightPx(value) {
  const parsed = parseInt(String(value), 10)
  if (!Number.isFinite(parsed)) return DEFAULT_HEADER_HEIGHT_PX
  return Math.max(MIN_HEADER_HEIGHT_PX, Math.min(MAX_HEADER_HEIGHT_PX, parsed))
}

function ensureHeaderMediaSettings() {
  const currentHeader = store.customization.media?.header || {}
  store.customization.media.header = {
    color: normalizeHeaderColor(currentHeader.color),
    textColor: normalizeHeaderTextColor(currentHeader.textColor),
    heightPx: normalizeHeaderHeightPx(currentHeader.heightPx),
  }

  return store.customization.media.header
}

const headerColorPreview = computed(() => normalizeHeaderColor(store.customization.media?.header?.color))
const headerTextColorPreview = computed(() => normalizeHeaderTextColor(store.customization.media?.header?.textColor))
const headerHeightPreview = computed(() => normalizeHeaderHeightPx(store.customization.media?.header?.heightPx))

// Cache-busted URL for the preview — forces browser re-fetch after a hosted
// image replacement while the clean URL stays in the store/DB.
const heroImagePreview = computed(() => {
  const url = store.customization.media.heroImageUrl || ''
  if (!url) return ''
  // Local data-URL shown while upload is in progress — use as-is
  if (url.startsWith('data:')) return url
  if (!store.heroImageCacheBust) return url
  const sep = url.includes('?') ? '&' : '?'
  return `${url}${sep}t=${store.heroImageCacheBust}`
})

// Truthy only when a real (non-data-URL) image is stored — drives the card UI
const heroImageStoredUrl = computed(() => {
  const url = store.customization.media.heroImageUrl || ''
  return url.startsWith('data:') ? '' : url
})

// Filename portion of the stored hosted image URL, shown in the info card
const heroImageFilename = computed(() => {
  const url = heroImageStoredUrl.value
  if (!url) return ''
  try { return new URL(url).pathname.split('/').pop() || url }
  catch { return url.split('/').pop() || url }
})

const videoPreviewEmbedUrl = computed(() => {
  const info = getVideoEmbedInfo(media.videoUrl, media.videoSource)
  return info.isValid ? info.embedUrl : ''
})

// ── Helpers ───────────────────────────────────────────────────────────────────

function flushStyles() {
  applyThemeOverrides(colors)
}

function stableSerialize(value) {
  if (Array.isArray(value)) {
    return `[${value.map((item) => stableSerialize(item)).join(',')}]`
  }

  if (value && typeof value === 'object') {
    const keys = Object.keys(value).sort()
    return `{${keys.map((key) => `${JSON.stringify(key)}:${stableSerialize(value[key])}`).join(',')}}`
  }

  return JSON.stringify(value)
}

function getCustomizationSnapshot() {
  return stableSerialize(store.customization)
}

function captureCustomizationSnapshot() {
  customizationEditSnapshot.value = getCustomizationSnapshot()
}

function resetTouchedFields() {
  touchedFields.value = new Set()
}

function persistEditorUiState() {
  persistCustomizerUiState({
    accessKey: activeOfficeId.value,
    activeTab: activeTab.value,
    isEditorReady: isEditorReady.value,
    selectedCustomizationRecord: selectedCustomizationRecord.value
      ? {
        id: selectedCustomizationRecord.value.id || null,
        name: selectedCustomizationRecord.value.name || '',
        customizationName: selectedCustomizationRecord.value.customizationName || selectedCustomizationRecord.value.name || '',
        state: selectedCustomizationRecord.value.state || '',
      }
      : null,
  })
}

function markFieldTouched(fieldName) {
  touchedFields.value = new Set([...touchedFields.value, fieldName])
  customizationSaveError.value = ''
}

function getCustomizationApiErrorMessage(error, fallbackMessage) {
  const responseData = error?.response?.data
  const apiMessage = responseData?.error || responseData?.message || error?.message || ''

  if (/invalid session/i.test(apiMessage)) {
    return 'Access denied: invalid session. Please make sure you are logged in to the Stonewood platform and that your session is still active.'
  }

  return apiMessage || fallbackMessage
}

function showCustomizationApiErrorAlert(message) {
  if (!/(invalid session|missing session)/i.test(message) || activeCustomizationErrorAlert === message) return

  activeCustomizationErrorAlert = message
  showCustomizerSwal({
    title: 'Customization access denied',
    text: message,
    icon: 'error',
    heightAuto: false,
  }).finally(() => {
    activeCustomizationErrorAlert = ''
  })
}

function resetTemplatePreviewToDefaults() {
  store.applyCustomizationPayload({})
  Object.assign(colors, { ...DEFAULT_BRAND_COLORS })
  clearThemeOverrides()
  media.heroImageFile = null
  media.heroImageUploadError = ''
  media.isReplacingHero = false
  media.heroImageReplaceSuccess = false
  media.videoSource = 'vimeo'
  media.videoUrl = DEFAULT_EMBED_VIDEO_URL
  media.overlayColor = null
  media.overlayOpacity = null
}

async function confirmDiscardUnsavedChanges() {
  if (!hasUnsavedChanges.value) return true

  const result = await showCustomizerSwal({
    title: 'Discard unsaved changes?',
    text: 'You have unsaved changes. If you go back to the customizations list, those changes will be lost.',
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: 'Discard changes',
    cancelButtonText: 'Keep editing',
    customClass: {
      confirmButton: 'customizer-swal-confirm customizer-swal-confirm--danger',
      cancelButton: 'customizer-swal-cancel',
    },
    reverseButtons: true,
    heightAuto: false,
  })

  return result.isConfirmed
}

async function confirmApplyChangesToPublishedCustomization() {
  if (store.customization.state !== 'published') return true

  const result = await showCustomizerSwal({
    title: 'Apply changes to a published customization?',
    text: 'This customization is currently published. Applying changes will make them visible live immediately.',
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: 'Apply live changes',
    cancelButtonText: 'Cancel',
    customClass: {
      confirmButton: 'customizer-swal-confirm customizer-swal-confirm--danger',
      cancelButton: 'customizer-swal-cancel',
    },
    reverseButtons: true,
    heightAuto: false,
  })

  return result.isConfirmed
}

async function confirmUnpublishCustomization() {
  const result = await showCustomizerSwal({
    title: 'Un-publish this customization?',
    text: 'This will un-publish the current configuration and move it back to preview state. It will no longer be served live as the published version.',
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: 'Un-publish',
    cancelButtonText: 'Cancel',
    customClass: {
      confirmButton: 'customizer-swal-confirm customizer-swal-confirm--danger',
      cancelButton: 'customizer-swal-cancel',
    },
    reverseButtons: true,
    heightAuto: false,
  })

  return result.isConfirmed
}

async function backToCustomizationList() {
  if (!(await confirmDiscardUnsavedChanges())) return

  // Always force a fresh branding fetch first — this picks up any state changes
  // (e.g. a customization that was just published will now come back with customization_id set)
  media.heroImageFile = null
  media.heroImageUploadError = ''
  media.isReplacingHero = false
  media.heroImageReplaceSuccess = false

  await loadBranding({ force: true, preserveLocalCustomization: false })

  // Now check the freshly-resolved brandingCustomizationId
  if (brandingCustomizationId.value) {
    // Branding returned a published customization — sync reactive state from it
    Object.assign(colors, getHydratedBrandColors(store.customization.theme))
    media.videoSource = store.customization.media.videoSource || 'vimeo'
    media.videoUrl = store.customization.media.videoUrl || DEFAULT_EMBED_VIDEO_URL
    media.overlayColor = store.customization.media.overlayColor ?? null
    media.overlayOpacity = store.customization.media.overlayOpacity ?? null
  } else {
    // No published customization — reset template to global defaults
    resetTemplatePreviewToDefaults()
  }

  isEditorReady.value = false
  selectedCustomizationRecord.value = null
  customizationEditSnapshot.value = ''
  resetTouchedFields()
  activeTab.value = 'brand'
  clearCustomizerUiState()

  await loadCustomizationRecords()
}

async function loadCustomizationRecords() {
  if (!isCustomizeFeatureEnabled.value) return

  const officeId = resolvedOfficeId.value
  const sessionId = resolvedSessionId.value
  customizationListRequestId += 1
  const requestId = customizationListRequestId

  customizationRecords.value = []
  customizationListError.value = ''

  if (!officeId || !sessionId) {
    if (officeId && !sessionId) {
      showCustomizationApiErrorAlert('Missing session. Please make sure you are logged in to the Stonewood platform and that your session is still active.')
    }

    customizationsLoading.value = false
    return
  }

  customizationsLoading.value = true

  try {
    const response = await axios.get(buildListCustomizationsUrl(officeId, sessionId))
    if (requestId !== customizationListRequestId || !isCustomizeFeatureEnabled.value) return

    if (response.data?.success === false) {
      throw new Error(response.data.error || response.data.message || 'Failed to load customization records.')
    }

    customizationRecords.value = normalizeCustomizationListResponse(response.data)
  } catch (error) {
    if (requestId !== customizationListRequestId || !isCustomizeFeatureEnabled.value) return

    customizationListError.value = getCustomizationApiErrorMessage(error, 'Failed to load customization records.')
    showCustomizationApiErrorAlert(customizationListError.value)
  } finally {
    if (requestId === customizationListRequestId) {
      customizationsLoading.value = false
    }
  }
}

function buildInitialCustomizationPayload() {
  const payload = new URLSearchParams()

  appendPayloadValue(payload, 'office_id', resolvedOfficeId.value)
  appendPayloadValue(payload, 'session_id', resolvedSessionId.value)
  appendPayloadValue(payload, 'customization_name', String(store.customization.name || '').trim())
  appendPayloadValue(payload, 'state', 'preview')
  appendPayloadValue(payload, 'author', resolvedAuthorId.value)
  appendPayloadValue(payload, 'theme', JSON.stringify(sanitizeBrandTheme(store.customization.theme || {})))
  appendPayloadValue(payload, 'media', JSON.stringify(buildCustomizedMediaPayload()))
  appendPayloadValue(payload, 'phone_required', store.customization.formOptions.phoneRequired ? 1 : 0)
  appendPayloadValue(payload, 'email_subject', store.customization.email.subject || '')
  appendPayloadValue(payload, 'email_body', store.customization.email.bodyHtml ?? '')
  appendPayloadValue(payload, 'attach_pdf', store.customization.email.attachPdfReport ? 1 : 0)

  return payload
}

function selectCustomizationRecord(record) {
  selectedCustomizationRecord.value = record
  store.applyCustomizationPayload(record.payload)
  store.customization.name = record.customizationName || record.name || ''
  clearThemeOverrides()
  applyThemeOverrides(store.customization.theme)
  activeTab.value = 'brand'
  isEditorReady.value = true
  captureCustomizationSnapshot()
  resetTouchedFields()
  // Reset hero image picker state for the newly loaded record
  media.heroImageFile = null
  media.heroImageUploadError = ''
  media.isReplacingHero = false
  media.heroImageReplaceSuccess = false
  // Force a cache-bust on load so the browser always re-fetches the stored image
  // rather than serving a stale cached version of the same URL.
  if (store.customization.media.heroImageUrl) {
    store.bumpHeroImageCacheBust()
  }
}

async function createCustomizationStub() {
  let seedPayload = {}

  if (brandingCustomizationId.value) {
    const result = await showCustomizerSwal({
      title: 'Start new customization from…',
      width: 680,
      html: `
        <p style="margin-bottom:8px">A published customization is currently active.<br>How would you like to start the new one?</p>
        <ul style="text-align:left;padding-left:1.4em;font-size:0.91em;color:#475569;line-height:1.7">
          <li><strong>Global defaults</strong> — clean slate using the standard Stonewood template.</li>
          <li><strong>Copy from published</strong> — inherit the same colors, media and email settings as the current published configuration.</li>
        </ul>`,
      icon: 'question',
      showCancelButton: true,
      showDenyButton: true,
      confirmButtonText: 'Start from global',
      denyButtonText: 'Copy from published',
      cancelButtonText: 'Cancel',
      reverseButtons: false,
      heightAuto: false,
    })

    if (result.isDismissed) return

    if (result.isDenied) {
      const published = JSON.parse(JSON.stringify(store.customization))
      seedPayload = {
        theme: published.theme,
        media: published.media,
        formOptions: published.formOptions,
        email: {
          subject: published.email.subject,
          bodyHtml: published.email.bodyHtml,
          attachPdfReport: published.email.attachPdfReport,
        },
      }
    }
  }

  selectedCustomizationRecord.value = {
    id: 'new-customization',
    name: 'New customization draft',
  }
  store.applyCustomizationPayload(seedPayload)
  store.customization.name = ''
  store.customization.state = 'preview'
  store.markCustomizationLoaded()

  // Always fully sync the visual layer so no stale published CSS lingers
  Object.assign(colors, getHydratedBrandColors(store.customization.theme))
  clearThemeOverrides()
  applyThemeOverrides(store.customization.theme)
  media.heroImageFile = null
  media.heroImageUploadError = ''
  media.isReplacingHero = false
  media.heroImageReplaceSuccess = false
  media.videoSource = store.customization.media.videoSource || 'vimeo'
  media.videoUrl = store.customization.media.videoUrl || DEFAULT_EMBED_VIDEO_URL
  media.overlayColor = store.customization.media.overlayColor ?? null
  media.overlayOpacity = store.customization.media.overlayOpacity ?? null

  createCustomizationError.value = ''
  activeTab.value = 'brand'
  isEditorReady.value = true
  captureCustomizationSnapshot()
  resetTouchedFields()
}

async function createCustomizationFromEditor() {
  if (isCreateCustomizationDisabled.value) return

  customizationSaveAction.value = 'create'
  customizationSaveError.value = ''
  createCustomizationError.value = ''

  try {
    const response = await axios.post(buildUpdateOfficeCustomizationUrl(resolvedSessionId.value, resolvedAuthorId.value), buildInitialCustomizationPayload(), {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    })

    if (response.data?.success === false) {
      throw new Error(response.data.error || response.data.message || 'Failed to create customization.')
    }

    const savedId = getSavedCustomizationId(response.data)
    if (!savedId) {
      throw new Error('Customization was created but no ID was returned.')
    }

    applySavedCustomizationId(savedId)
    store.customization.state = 'preview'
    captureCustomizationSnapshot()
    resetTouchedFields()
    await loadCustomizationRecords()
  } catch (error) {
    createCustomizationError.value = getCustomizationApiErrorMessage(error, 'Failed to create customization.')
    showCustomizationApiErrorAlert(createCustomizationError.value)
  } finally {
    customizationSaveAction.value = ''
  }
}

watch(
  [isCustomizeFeatureEnabled, resolvedOfficeId, resolvedSessionId],
  ([enabled, officeId, sessionId]) => {
    customizationListRequestId += 1

    if (!enabled) {
      isEditorReady.value = false
      selectedCustomizationRecord.value = null
      activeOfficeId.value = ''
      createCustomizationError.value = ''
      customizationEditSnapshot.value = ''
      resetTouchedFields()
      customizationRecords.value = []
      customizationListError.value = ''
      customizationsLoading.value = false
      clearCustomizerUiState()
      return
    }

    const accessKey = `${officeId}:${sessionId}`

    if (activeOfficeId.value !== accessKey) {
      const hasExistingAccessKey = Boolean(activeOfficeId.value)

      isEditorReady.value = false
      selectedCustomizationRecord.value = null
      customizationEditSnapshot.value = ''
      resetTouchedFields()
      activeOfficeId.value = accessKey

      if (hasExistingAccessKey) {
        clearCustomizerUiState()
      }
    }

    if (isEditorReady.value) return
    loadCustomizationRecords()
  },
  { immediate: true }
)

watch(
  [activeTab, isEditorReady, selectedCustomizationRecord],
  () => {
    if (!isCustomizeFeatureEnabled.value) return
    persistEditorUiState()
  },
  { deep: true }
)

// ── Core: apply a color live ──────────────────────────────────────────────────
function applyColor(varName, hex) {
  markFieldTouched('theme')
  colors[varName] = hex
  store.customization.theme = {
    ...(store.customization.theme || {}),
    [varName]: hex,
  }
  flushStyles()
}

// ── Reset ─────────────────────────────────────────────────────────────────────
function resetColors() {
  markFieldTouched('theme')
  Object.assign(colors, { ...DEFAULT_BRAND_COLORS })
  store.customization.theme = {}
  clearThemeOverrides()
}

// ── Copy CSS vars ─────────────────────────────────────────────────────────────
async function resetAllToGlobal() {
  const result = await showCustomizerSwal({
    title: 'Reset all fields to global defaults?',
    text: 'This will wipe all brand colors, media, and email settings and replace them with the Stonewood global template. Any unsaved changes will be lost.',
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: 'Reset everything',
    cancelButtonText: 'Cancel',
    customClass: {
      confirmButton: 'customizer-swal-confirm customizer-swal-confirm--danger',
      cancelButton: 'customizer-swal-cancel',
    },
    reverseButtons: true,
    heightAuto: false,
  })

  if (!result.isConfirmed) return

  resetTemplatePreviewToDefaults()
  markFieldTouched('theme')
  markFieldTouched('media')
  markFieldTouched('email')
  markFieldTouched('phone_required')
  markFieldTouched('attach_pdf')
}

// ── Copy CSS vars ─────────────────────────────────────────────────────────────
function copyCSS() {
  const lines = Object.entries(colors)
    .map(([key, hex]) => `  --v-theme-${key}: ${hexToRgbComponents(hex)};`)
    .join('\n')
  const css = `:root {\n${lines}\n}`
  navigator.clipboard.writeText(css).catch(() => {})
}

watch(
  () => store.customization.theme,
  (theme) => {
    Object.assign(colors, getHydratedBrandColors(theme))
  },
  { deep: true }
)

watch(
  () => store.customization.media,
  (storeMedia) => {
    media.videoSource = storeMedia?.videoSource || 'vimeo'
    media.videoUrl = storeMedia?.videoUrl || DEFAULT_EMBED_VIDEO_URL
    media.overlayColor = storeMedia?.overlayColor ?? null
    media.overlayOpacity = storeMedia?.overlayOpacity ?? null
  },
  { deep: true }
)

function getUploadUrlFromResponse(data) {
  if (!data) return ''
  if (typeof data === 'string') return data

  const directUrl =
    data.url ||
    data.fileUrl ||
    data.location ||
    data.imageUrl ||
    data.image_url ||
    data.data?.url ||
    data.data?.fileUrl ||
    data.data?.location ||
    data.data?.imageUrl ||
    data.data?.image_url ||
    ''

  return directUrl ? String(directUrl) : ''
}

function getPersistableHeroImageUrl() {
  const url = String(store.customization.media.heroImageUrl || '').trim()
  return url.startsWith('data:') ? '' : url
}

function getSingleFile(value) {
  if (Array.isArray(value)) return value[0] || null
  return value || null
}

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result || '')
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(file)
  })
}

async function onHeroImageSelected(value) {
  const file = getSingleFile(value)
  const wasReplacing = media.isReplacingHero
  const previousStoredUrl = getPersistableHeroImageUrl()
  media.heroImageFile = file
  media.heroImageUploadError = ''
  media.heroImageReplaceSuccess = false
  markFieldTouched('media')

  if (!file) {
    store.customization.media.heroImageUrl = ''
    return
  }

  // Show a local data-URL preview immediately for instant visual feedback
  // while the real upload is in progress.
  const localPreviewUrl = await readFileAsDataUrl(file)
  store.customization.media.heroImageUrl = localPreviewUrl

  media.isUploadingHero = true
  try {
    const sessionId = String(resolvedSessionId.value || '').trim()
    if (!sessionId) {
      throw new Error('Missing session ID. Please refresh from a valid customization session.')
    }

    const customizationId = await ensureCustomizationIdForHeroUpload()
    const formData = new FormData()
    formData.append('img', file)
    formData.append('id', String(customizationId))

    const response = await axios.post(buildUploadCustomizationImageUrl(sessionId), formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })

    const uploadedUrl = getUploadUrlFromResponse(response.data)
    if (!uploadedUrl) {
      throw new Error('Upload succeeded but no public URL was returned.')
    }

    store.customization.media.heroImageUrl = uploadedUrl
    if (previousStoredUrl !== uploadedUrl) {
      markFieldTouched('media')
    }
    store.bumpHeroImageCacheBust()
    media.isReplacingHero = false
    media.heroImageFile = null
    if (wasReplacing) media.heroImageReplaceSuccess = true
  } catch (error) {
    media.heroImageUploadError =
      getCustomizationApiErrorMessage(error, 'Failed to upload hero image.')
    store.customization.media.heroImageUrl = previousStoredUrl
  } finally {
    media.isUploadingHero = false
  }
}

async function ensureCustomizationIdForHeroUpload() {
  if (hasPersistedCustomizationId.value) return String(store.customization.id)

  if (!isCustomizationNameValid.value) {
    throw new Error('Name this customization before uploading a hero image.')
  }

  const payload = buildOfficeCustomizationPayload('preview')
  const response = await axios.post(buildUpdateOfficeCustomizationUrl(resolvedSessionId.value, resolvedAuthorId.value), payload, {
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
  })

  if (response.data?.success === false) {
    throw new Error(response.data.error || response.data.message || 'Failed to create customization before upload.')
  }

  const savedId = getSavedCustomizationId(response.data)
  if (!savedId) {
    throw new Error('Customization was saved but no ID was returned for image upload.')
  }

  applySavedCustomizationId(savedId)
  store.customization.state = store.customization.state || 'preview'
  captureCustomizationSnapshot()
  resetTouchedFields()
  await loadCustomizationRecords()

  return String(savedId)
}

function removeHeroImage() {
  markFieldTouched('media')
  media.heroImageFile = null
  media.heroImageUploadError = ''
  media.isReplacingHero = false
  media.heroImageReplaceSuccess = false
  store.customization.media.heroImageUrl = ''
}

function cancelReplaceHero() {
  media.heroImageFile = null
  media.heroImageUploadError = ''
  media.isReplacingHero = false
}

function videoValidationError() {
  const url = String(media.videoUrl || '').trim()
  if (!url) return ''

  const info = getVideoEmbedInfo(url, media.videoSource)
  if (info.isValid) return ''
  return `Please enter a valid ${media.videoSource === 'youtube' ? 'YouTube' : 'Vimeo'} URL.`
}

const videoUrlRules = [() => videoValidationError() || true]

function applyVideoSelection() {
  const url = String(media.videoUrl || '').trim()
  markFieldTouched('media')

  if (!url) {
    store.customization.media.videoSource = media.videoSource
    store.customization.media.videoUrl = ''
    return
  }

  const info = getVideoEmbedInfo(url, media.videoSource)
  if (!info.isValid) return

  store.customization.media.videoSource = media.videoSource
  store.customization.media.videoUrl = url
}

function isValidOverlayHex(val) {
  return /^#[0-9A-Fa-f]{6}$/.test(val)
}

function onOverlayColorInput(hex) {
  if (!isValidOverlayHex(hex)) return
  media.overlayColor = hex
  store.customization.media.overlayColor = hex
  if (media.overlayOpacity === null) {
    media.overlayOpacity = 40
    store.customization.media.overlayOpacity = 40
  }
  markFieldTouched('media')
}

function onOverlayOpacityInput(val) {
  const parsed = parseInt(String(val), 10)
  if (isNaN(parsed) || parsed < 0 || parsed > 100) return
  media.overlayOpacity = parsed
  store.customization.media.overlayOpacity = parsed
  markFieldTouched('media')
}

function onHeaderColorInput(hex) {
  if (!isValidOverlayHex(hex)) return
  const header = ensureHeaderMediaSettings()
  header.color = hex
  markFieldTouched('media')
}

function onHeaderTextColorInput(hex) {
  if (!isValidOverlayHex(hex)) return
  const header = ensureHeaderMediaSettings()
  header.textColor = hex
  markFieldTouched('media')
}

function onHeaderHeightInput(value) {
  const header = ensureHeaderMediaSettings()
  header.heightPx = normalizeHeaderHeightPx(value)
  markFieldTouched('media')
}

function clearOverlay() {
  media.overlayColor = null
  media.overlayOpacity = null
  store.customization.media.overlayColor = null
  store.customization.media.overlayOpacity = null
  markFieldTouched('media')
}

function resetMedia() {
  markFieldTouched('media')
  media.heroImageFile = null
  media.heroImageUploadError = ''
  media.isReplacingHero = false
  media.heroImageReplaceSuccess = false
  media.videoSource = 'vimeo'
  media.videoUrl = DEFAULT_EMBED_VIDEO_URL
  media.overlayColor = null
  media.overlayOpacity = null

  store.customization.media.heroImageUrl = ''
  store.customization.media.videoSource = 'vimeo'
  store.customization.media.videoUrl = DEFAULT_EMBED_VIDEO_URL
  store.customization.media.overlayColor = null
  store.customization.media.overlayOpacity = null
}

function appendPayloadValue(payload, key, value) {
  if (value === null || value === undefined) return

  payload.set(key, String(value))
}

function buildCustomizedMediaPayload() {
  const backgroundImage = {
    url: getPersistableHeroImageUrl() || null,
  }

  const header = ensureHeaderMediaSettings()

  const overlayColor = store.customization.media.overlayColor ?? null
  const overlayOpacity = store.customization.media.overlayOpacity ?? null
  if (overlayColor !== null) backgroundImage.overlayColor = overlayColor
  if (overlayOpacity !== null) backgroundImage.overlayOpacity = overlayOpacity

  return {
    video: {
      source: store.customization.media.videoSource || '',
      url: store.customization.media.videoUrl || '',
    },
    backgroundImage,
    header: {
      color: normalizeHeaderColor(header.color),
      textColor: normalizeHeaderTextColor(header.textColor),
      heightPx: normalizeHeaderHeightPx(header.heightPx),
    },
  }
}

function buildOfficeCustomizationPayload(state, options = {}) {
  const {
    includeCustomizationFields = true,
  } = options

  const payload = new URLSearchParams()
  const fields = touchedFields.value

  appendPayloadValue(payload, 'office_id', resolvedOfficeId.value)
  appendPayloadValue(payload, 'session_id', resolvedSessionId.value)
  appendPayloadValue(payload, 'customization_name', String(store.customization.name || '').trim())
  if (state) appendPayloadValue(payload, 'state', state)
  appendPayloadValue(payload, 'id', store.customization.id || null)
  appendPayloadValue(payload, 'author', resolvedAuthorId.value)

  if (includeCustomizationFields) {
    appendPayloadValue(payload, 'theme', fields.has('theme')
      ? JSON.stringify(sanitizeBrandTheme(store.customization.theme || {}))
      : null)
    appendPayloadValue(payload, 'media', fields.has('media')
      ? JSON.stringify(buildCustomizedMediaPayload())
      : null)
    appendPayloadValue(payload, 'phone_required', fields.has('phone_required') ? (store.customization.formOptions.phoneRequired ? 1 : 0) : null)
    appendPayloadValue(payload, 'email_subject', fields.has('email_subject') ? (store.customization.email.subject || '') : null)
    appendPayloadValue(payload, 'email_body', fields.has('email_body') ? (store.customization.email.bodyHtml ?? '') : null)
    appendPayloadValue(payload, 'attach_pdf', fields.has('attach_pdf') ? (store.customization.email.attachPdfReport ? 1 : 0) : null)
  }

  return payload
}

function applySavedCustomizationId(id) {
  if (!id) return

  store.customization.id = id

  selectedCustomizationRecord.value = {
    ...(selectedCustomizationRecord.value || {}),
    id,
    name: store.customization.name,
    customizationName: store.customization.name,
  }
}

async function saveCustomization(state) {
  if (state === 'published' ? isPublishSaveDisabled.value : isPreviewSaveDisabled.value) return

  if (state === 'preview' && !(await confirmApplyChangesToPublishedCustomization())) return

  customizationSaveAction.value = state
  customizationSaveError.value = ''

  const isFirstSave = !hasPersistedCustomizationId.value
  const payloadState = state === 'published'
    ? 'published'
    : (isFirstSave ? 'preview' : '')

  try {
    const payload = buildOfficeCustomizationPayload(payloadState, {
      includeCustomizationFields: state !== 'published',
    })
    const response = await axios.post(buildUpdateOfficeCustomizationUrl(resolvedSessionId.value, resolvedAuthorId.value), payload, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    })

    if (response.data?.success === false) {
      throw new Error(response.data.error || response.data.message || 'Failed to save customization.')
    }

    applySavedCustomizationId(getSavedCustomizationId(response.data))

    if (state === 'published') {
      store.customization.state = 'published'
    } else if (isFirstSave) {
      store.customization.state = 'preview'
    }

    captureCustomizationSnapshot()
    resetTouchedFields()
    await loadCustomizationRecords()

    await showCustomizerSwal({
      title: state === 'published' ? 'Customization published' : 'Changes applied',
      text: state === 'published' ? 'This customization is now published.' : 'Your changes were saved.',
      icon: 'success',
      heightAuto: false,
      timer: 1800,
    })
  } catch (error) {
    customizationSaveError.value = getCustomizationApiErrorMessage(error, 'Failed to save customization.')
    showCustomizationApiErrorAlert(customizationSaveError.value)
  } finally {
    customizationSaveAction.value = ''
  }
}

async function unpublishCustomization() {
  if (isUnpublishDisabled.value) return
  if (!(await confirmUnpublishCustomization())) return

  customizationSaveAction.value = 'unpublished'
  customizationSaveError.value = ''

  try {
    const payload = buildOfficeCustomizationPayload('preview', {
      includeCustomizationFields: false,
    })
    const response = await axios.post(buildUpdateOfficeCustomizationUrl(resolvedSessionId.value, resolvedAuthorId.value), payload, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    })

    if (response.data?.success === false) {
      throw new Error(response.data.error || response.data.message || 'Failed to un-publish customization.')
    }

    applySavedCustomizationId(getSavedCustomizationId(response.data))
    store.customization.state = 'preview'
    captureCustomizationSnapshot()
    resetTouchedFields()
    await loadCustomizationRecords()

    await showCustomizerSwal({
      title: 'Customization un-published',
      text: 'This customization is now in preview state.',
      icon: 'success',
      heightAuto: false,
      timer: 1800,
    })
  } catch (error) {
    customizationSaveError.value = getCustomizationApiErrorMessage(error, 'Failed to un-publish customization.')
    showCustomizationApiErrorAlert(customizationSaveError.value)
  } finally {
    customizationSaveAction.value = ''
  }
}
</script>

<style scoped>
/* FAB — sticks to bottom-right, clears the drawer when open */
.customize-fab {
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 2001;
}

.customize-drawer {
  top: 0 !important;
  height: 100vh !important;
}

.email-preview-frame-wrapper {
  overflow: hidden;
}

.email-preview-frame {
  width: 100%;
  min-height: 360px;
  display: block;
  border: none;
}

.customize-drawer :deep(.v-navigation-drawer__content) {
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

.customizer-sticky-header {
  position: sticky;
  top: 0;
  z-index: 3;
  flex: 0 0 auto;
  background: rgb(var(--v-theme-customizer-surface));
  box-shadow: 0 8px 20px rgb(var(--v-theme-on-customizer-surface) / 0.08);
}

/* Scrollable tab content fills remaining drawer height */
.customizer-editor-shell {
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
}

.customizer-create-overlay {
  z-index: 30;
}

.customizer-create-overlay__card {
  width: min(420px, calc(100vw - 48px));
}

.customizer-window {
  flex: 1 1 auto;
  min-height: 0;
}

.customization-selector {
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
}

.customization-selector-card {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}

.customization-selector-card__header {
  flex: 0 0 auto;
}

.customization-selector-card__body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
}

.customization-selector-card__footer {
  flex: 0 0 auto;
}

.media-preview {
  border-radius: 8px;
  max-height: 180px;
  border: 1px solid rgb(var(--v-theme-on-customizer-surface) / 0.15);
}

.hero-stored-card {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border: 1px solid rgb(var(--v-theme-customizer-success) / 0.3);
  border-radius: 8px;
  background: rgb(var(--v-theme-customizer-success) / 0.06);
}

.hero-stored-card__text {
  flex: 1;
  min-width: 0;
}

.media-video-wrap {
  position: relative;
  width: 100%;
  padding-top: 56.25%;
  border-radius: 8px;
  overflow: hidden;
}

.media-video-wrap iframe {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}

.overlay-color-swatch {
  display: inline-block;
  width: 36px;
  height: 36px;
  border-radius: 6px;
  border: 1px solid rgb(var(--v-theme-on-customizer-surface) / 0.3);
  cursor: pointer;
  flex-shrink: 0;
  position: relative;
  overflow: hidden;
}

.hidden-color-input {
  position: absolute;
  inset: 0;
  opacity: 0;
  cursor: pointer;
  width: 100%;
  height: 100%;
  padding: 0;
  border: none;
}

.customization-record-card-list {
  display: grid;
  gap: 12px;
}

.customization-record-card {
  border-color: rgb(var(--v-theme-on-customizer-surface) / 0.12);
  background: rgb(var(--v-theme-customizer-surface));
}

.customization-record-card__title {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 14px 8px;
}

.customization-record-card__title .min-w-0 {
  min-width: 0;
}

.customization-record-card__name {
  overflow: hidden;
  color: rgb(var(--v-theme-on-customizer-surface));
  font-size: 0.95rem;
  font-weight: 700;
  line-height: 1.25;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.customization-record-card__meta {
  display: grid;
  gap: 8px;
  padding: 0 14px 10px;
}

.customization-record-card__meta > div {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  color: rgb(var(--v-theme-on-customizer-surface) / 0.64);
  font-size: 0.78rem;
  line-height: 1.2;
}

.customization-record-card__meta strong {
  color: rgb(var(--v-theme-on-customizer-surface));
  font-weight: 600;
  text-align: right;
}

.customization-record-card__actions {
  padding: 0 14px 14px;
}

.customization-edit-header {
  border: 1px solid rgb(var(--v-theme-on-customizer-surface) / 0.12);
  border-radius: 10px;
}

.header-settings-card,
.header-height-card {
  border-color: rgb(var(--v-theme-on-customizer-surface) / 0.18) !important;
  background: rgb(var(--v-theme-customizer-surface)) !important;
}

.customizer-save-footer {
  position: sticky;
  bottom: 0;
  z-index: 5;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 10px;
  padding: 12px;
  border-top: 1px solid rgb(var(--v-theme-on-customizer-surface) / 0.12);
  background: rgb(var(--v-theme-customizer-surface));
  box-shadow: 0 -8px 20px rgb(var(--v-theme-on-customizer-surface) / 0.08);
}

.customizer-save-footer__meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  color: rgb(var(--v-theme-on-customizer-surface) / 0.72);
  font-size: 0.75rem;
  line-height: 1.2;
}

.customizer-save-footer__meta > span {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.customizer-save-footer__actions {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}

@media (max-width: 420px) {
  .customizer-save-footer__meta {
    align-items: flex-start;
    flex-direction: column;
  }

  .customizer-save-footer__meta > span {
    white-space: normal;
  }

  .customizer-save-footer__actions {
    grid-template-columns: 1fr;
  }
}
</style>

<!-- Global (unscoped) isolation block — pins the drawer's internal theme vars
     so the editor UI never reacts to the user's live colour picks.           -->
<style>
.customize-drawer,
.customize-drawer * {
  --v-theme-background: var(--v-theme-customizer-background, 248 250 252) !important;
  --v-theme-on-background: var(--v-theme-on-customizer-background, 15 23 42) !important;
  --v-theme-surface: var(--v-theme-customizer-surface, 255 255 255) !important;
  --v-theme-surface-bright: var(--v-theme-customizer-surface, 255 255 255) !important;
  --v-theme-surface-light: var(--v-theme-customizer-surface, 255 255 255) !important;
  --v-theme-surface-variant: var(--v-theme-customizer-surface-variant, 241 245 249) !important;
  --v-theme-on-surface: var(--v-theme-on-customizer-surface, 15 23 42) !important;
  --v-theme-on-surface-bright: var(--v-theme-on-customizer-surface, 15 23 42) !important;
  --v-theme-on-surface-light: var(--v-theme-on-customizer-surface, 15 23 42) !important;
  --v-theme-on-surface-variant: var(--v-theme-on-customizer-surface-variant, 51 65 85) !important;
  --v-theme-primary: var(--v-theme-customizer-primary, 30 41 59) !important;
  --v-theme-on-primary: var(--v-theme-on-customizer-primary, 255 255 255) !important;
  --v-theme-secondary: var(--v-theme-customizer-secondary, 71 85 105) !important;
  --v-theme-on-secondary: var(--v-theme-on-customizer-secondary, 255 255 255) !important;
  --v-theme-accent: var(--v-theme-customizer-accent, 241 194 51) !important;
  --v-theme-on-accent: var(--v-theme-on-customizer-accent, 30 41 59) !important;
  --v-theme-error: var(--v-theme-customizer-error, 221 51 51) !important;
  --v-theme-on-error: var(--v-theme-on-customizer-error, 255 255 255) !important;
  --v-theme-info: var(--v-theme-customizer-info, 37 99 235) !important;
  --v-theme-on-info: var(--v-theme-on-customizer-info, 255 255 255) !important;
  --v-theme-success: var(--v-theme-customizer-success, 22 163 74) !important;
  --v-theme-on-success: var(--v-theme-on-customizer-success, 255 255 255) !important;
  --v-theme-warning: var(--v-theme-customizer-warning, 217 119 6) !important;
  --v-theme-on-warning: var(--v-theme-on-customizer-warning, 255 255 255) !important;
}

.customizer-swal-container {
  --v-theme-background: var(--v-theme-customizer-background, 248 250 252) !important;
  --v-theme-on-background: var(--v-theme-on-customizer-background, 15 23 42) !important;
  --v-theme-surface: var(--v-theme-customizer-surface, 255 255 255) !important;
  --v-theme-on-surface: var(--v-theme-on-customizer-surface, 15 23 42) !important;
  --v-theme-surface-variant: var(--v-theme-customizer-surface-variant, 241 245 249) !important;
  --v-theme-on-surface-variant: var(--v-theme-on-customizer-surface-variant, 51 65 85) !important;
  --v-theme-primary: var(--v-theme-customizer-primary, 30 41 59) !important;
  --v-theme-on-primary: var(--v-theme-on-customizer-primary, 255 255 255) !important;
  --v-theme-error: var(--v-theme-customizer-error, 221 51 51) !important;
  --v-theme-on-error: var(--v-theme-on-customizer-error, 255 255 255) !important;
  --v-theme-warning: var(--v-theme-customizer-warning, 217 119 6) !important;
  --v-theme-on-warning: var(--v-theme-on-customizer-warning, 255 255 255) !important;
  --v-theme-success: var(--v-theme-customizer-success, 22 163 74) !important;
  --v-theme-on-success: var(--v-theme-on-customizer-success, 255 255 255) !important;
}

.customizer-swal-popup {
  border-radius: 16px;
  background: rgb(var(--v-theme-surface)) !important;
  color: rgb(var(--v-theme-on-surface)) !important;
}

.customizer-swal-popup .swal2-title,
.customizer-swal-popup .swal2-html-container {
  color: rgb(var(--v-theme-on-surface)) !important;
}

.customizer-swal-confirm,
.customizer-swal-deny,
.customizer-swal-cancel {
  min-width: 96px;
  margin: 0 4px;
  padding: 10px 18px;
  border: 0;
  border-radius: 999px;
  font-weight: 700;
  line-height: 1.2;
  cursor: pointer;
}

.customizer-swal-confirm {
  background: rgb(var(--v-theme-primary)) !important;
  color: rgb(var(--v-theme-on-primary)) !important;
}

.customizer-swal-confirm--danger {
  background: rgb(var(--v-theme-error)) !important;
  color: rgb(var(--v-theme-on-error)) !important;
}

.customizer-swal-deny {
  background: rgb(var(--v-theme-secondary)) !important;
  color: rgb(var(--v-theme-on-secondary)) !important;
}

.customizer-swal-cancel {
  background: rgb(var(--v-theme-surface-variant)) !important;
  color: rgb(var(--v-theme-on-surface-variant)) !important;
}
</style>
